import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Box,
  Button,
  Grid,
  CircularProgress,
  TextField,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { Breadcrumb, SimpleCard } from "app/components";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import {
  GETALLUSERS,
  getAllAttendance,
  deleteCheckIn,
} from "app/redux/actions";
import { toast } from "react-toastify";
import { capitalizeFirstLetter } from "app/assets/genericActions";
import styled from "styled-components";

const HorizontalTable = styled.div`
  overflow-x: auto;
  @media (max-width: 600px) {
    width: 350%;
    overflow-x: auto;
  }
`;

function AllCheck() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const loggedInUser = useSelector((state) => state.user.user);
  const allUsers = useSelector((state) => state.user.allUsers?.users || []);
  const { attendanceData, loading } = useSelector((state) => state.attendance);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [checkInToDelete, setCheckInToDelete] = useState(null);
  const [userList, setUserList] = useState([]);

  const isAdmin = useMemo(
    () => loggedInUser?.userType === "admin",
    [loggedInUser]
  );
  const isTeamLead = useMemo(
    () => loggedInUser?.userType === "teamlead",
    [loggedInUser]
  );
  const isAdminOrLead = isAdmin || isTeamLead;
  const pageMode = useMemo(() => {
    if (location.pathname.includes("/my-checks")) return "my";
    if (location.pathname.includes("/team-checks")) return "team";
    return "all";
  }, [location.pathname]);

  useEffect(() => {
    if (loggedInUser?.token && isAdminOrLead) {
      dispatch(
        GETALLUSERS(
          {
            page: 1,
            limit: 1000,
            teamLeadId: isTeamLead ? loggedInUser.id : null,
          },
          loggedInUser
        )
      );
    }
  }, [dispatch, loggedInUser, isAdminOrLead, isTeamLead]);

  useEffect(() => {
    if (isAdmin && allUsers.length) {
      setUserList([{ id: null, name: "All Employees" }, ...allUsers]);
    } else if (isTeamLead && allUsers.length) {
      const teamMembers = allUsers.filter(
        (user) => user.teamLeadId === loggedInUser.id
      );
      setUserList([{ id: null, name: "All Team Members" }, ...teamMembers]);
    }
  }, [allUsers, isAdmin, isTeamLead, loggedInUser]);

  useEffect(() => {
    if (!loggedInUser?.token) return;
    const filters = {};
    if (selectedMonth) {
      filters.date = selectedMonth;
    }
    if (pageMode === "my") {
      filters.userId = loggedInUser.id;
    } else if (pageMode === "team") {
      filters.teamLeadId = loggedInUser.id;
      if (selectedEmployee?.id) {
        filters.userId = selectedEmployee.id;
        delete filters.teamLeadId;
      }
    } else {
      if (isAdminOrLead) {
        if (selectedEmployee?.id) {
          filters.userId = selectedEmployee.id;
        }
      } else {
        filters.userId = loggedInUser.id;
      }
    }
    dispatch(getAllAttendance(filters));
  }, [
    dispatch,
    loggedInUser,
    selectedMonth,
    selectedEmployee,
    isAdminOrLead,
    pageMode,
    location,
  ]);

  const handleEdit = (recordId) => {
    navigate(`/edit-check/${recordId}`);
  };
  const handleViewLogs = (userId, date) => {
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    navigate(`/machine-logs/${userId}/${formattedDate}`);
  };

  const handleDelete = (id) => {
    if (isAdmin) {
      setCheckInToDelete(id);
      setOpenDialog(true);
    } else {
      toast.error("You do not have permission to delete this record.");
    }
  };
  const handleConfirmDelete = () => {
    if (checkInToDelete) {
      dispatch(deleteCheckIn(checkInToDelete));
      setOpenDialog(false);
    }
  };

  const formatBreakTime = (totalSeconds) => {
    if (!totalSeconds || isNaN(totalSeconds) || totalSeconds <= 0) {
      return "0 hr 0 min";
    }
    if (totalSeconds >= 3600) {
      const hours = Math.floor(totalSeconds / 3600);
      const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
      return `${hours} hr ${remainingMinutes} min`;
    } else {
      const minutes = Math.floor(totalSeconds / 60);
      return `${minutes} min`;
    }
  };

  const formatWorkingHours = (decimalHours) => {
    if (decimalHours == null || isNaN(decimalHours)) {
      return "00:00";
    }
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`;
  };

  return (
    <SimpleCard>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Breadcrumb
          routeSegments={[
            {
              name:
                pageMode === "my"
                  ? "My Checks"
                  : pageMode === "team"
                  ? "Team Checks"
                  : "All Checks",
            },
          ]}
        />
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/create-check")}
          >
            Create Check
          </Button>
        )}
      </Box>
      <Box padding={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={isAdminOrLead ? 6 : 12}>
            <TextField
              fullWidth
              label="Select Month"
              type="month"
              value={selectedMonth || ""}
              onChange={(e) => setSelectedMonth(e.target.value || null)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {(isAdmin || pageMode === "team") && (
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={userList}
                getOptionLabel={(option) => option.name || ""}
                value={selectedEmployee}
                onChange={(event, newValue) => setSelectedEmployee(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Employee"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            paddingBottom: "70px",
            marginTop: "16px",
          }}
        >
          <HorizontalTable>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>Employee</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Check In</TableCell>
                    <TableCell>Check Out</TableCell>
                    <TableCell>Break</TableCell>
                    <TableCell>Working Hours</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>On Break</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : !attendanceData || attendanceData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No records found for the selected filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    attendanceData.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell sx={{ pl: 4 }}>
                          {capitalizeFirstLetter(record.User?.name) || "N/A"}
                        </TableCell>
                        <TableCell>
                          {dayjs(record.date).format("YYYY-MM-DD")}
                        </TableCell>
                        <TableCell>
                          {record.checkIn
                            ? dayjs(record.checkIn).format("hh:mm A")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {record.checkOut
                            ? dayjs(record.checkOut).format("hh:mm A")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {formatBreakTime(record.breakTime)}
                        </TableCell>
                        <TableCell>
                          {record.workingHours != null
                            ? formatWorkingHours(record.workingHours)
                            : "N/A"}
                        </TableCell>
                        <TableCell>{record.notes || "-"}</TableCell>
                        <TableCell>{record.isBreak ? "Yes" : "No"}</TableCell>
                        <TableCell>
                        
                          {isAdmin && (
                            <>
                            
                              {isAdmin && (
                                <Tooltip title="View Machine Logs">
                                  <IconButton
                                    onClick={() =>
                                      handleViewLogs(record.userId, record.date)
                                    }
                                    color="default"
                                  >
                                    <HistoryIcon />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <IconButton
                                onClick={() => handleEdit(record.id)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDelete(record.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </HorizontalTable>
        </Box>
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this check-in record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </SimpleCard>
  );
}

export default AllCheck;